import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminEventKey, QuestionResult, Quiz, ServerEvent } from '@floorpoll/shared';
import { FormError } from '../../components/Form.js';
import { ResultBars } from '../../components/ResultBars.js';
import { ApiError } from '../../lib/api.js';
import { quizApi } from '../../lib/quizApi.js';
import { track } from '../../lib/track.js';
import { useCountdown } from '../../lib/useCountdown.js';
import { useLiveQuiz } from '../../lib/useLiveQuiz.js';
import { QandaPanel } from './QandaPanel.js';

/** Stories #20 and #21 — the presenter's controls. */
export function Presenter() {
  const { quizId = '' } = useParams();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [results, setResults] = useState<QuestionResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const resync = useCallback(async () => {
    try {
      const loaded = await quizApi.get(quizId);
      setQuiz(loaded);

      // Results for the screen being presented, so a reconnect shows current
      // numbers rather than waiting for the next vote.
      const full = await quizApi.results(quizId);
      setResults(full.screens[loaded.currentScreenIndex]?.questions ?? []);

      // The Q&A queue is the panel's own state, and this is the one path that
      // recovers a dropped socket. Fired after the reads above rather than
      // beside them: a failure here must not cost the presenter their results.
      qandaReloadRef.current?.();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load that poll.');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    void resync();
  }, [resync]);

  /**
   * The server's clock, from whichever event last carried it (#77).
   *
   * Kept beside the quiz rather than on it: this view holds a `Quiz`, the
   * private shape, which has no place for a per-payload timestamp. Null until
   * the first event lands — `GET /quizzes/{quizId}` carries no server time — and
   * the countdown falls back to this laptop's own clock until it does.
   */
  const [serverTime, setServerTime] = useState<string | null>(null);

  // Read inside the socket callback, which must not close over stale state.
  const screenIndexRef = useRef(0);
  screenIndexRef.current = quiz?.currentScreenIndex ?? 0;

  /**
   * #118 — the Q&A panel's own handler, held in a ref for the same reason
   * `screenIndexRef` is: `onEvent` below is built once and must not close over
   * a stale one.
   *
   * One socket serves both. `useLiveQuiz` is opened here and the panel hands
   * its handler up rather than opening a second — two connections would mean
   * the room's every vote fanned out to this laptop twice, and the connection
   * table carrying two rows per presenter.
   */
  const qandaHandlerRef = useRef<((event: ServerEvent) => void) | null>(null);
  const registerQandaHandler = useCallback((handler: (event: ServerEvent) => void) => {
    qandaHandlerRef.current = handler;
  }, []);

  // ...and its re-read, called from `resync` below. A dropped socket is
  // recovered by useLiveQuiz calling onResync and by nothing else — no event
  // is broadcast when a connection comes back — so the queue has to ride the
  // page's own recovery or it silently stops filling.
  const qandaReloadRef = useRef<(() => void) | null>(null);
  const registerQandaReload = useCallback((reload: () => void) => {
    qandaReloadRef.current = reload;
  }, []);

  const onEvent = useCallback((event: ServerEvent) => {
    switch (event.type) {
      case 'vote':
        // Drop a payload that raced a screen change.
        if (event.currentScreenIndex === screenIndexRef.current) setResults(event.results);
        break;
      case 'screen':
        setResults(event.results ?? []);
        setServerTime(event.serverTime);
        setQuiz((current) =>
          current
            ? {
                ...current,
                currentScreenIndex: event.currentScreenIndex,
                // Advancing resets both server-side; copied here because this
                // reducer merges a fixed field list off the event. The screen's
                // clock (#77) rides along for the same reason.
                resultsVisible: event.resultsVisible,
                votingClosed: event.votingClosed,
                screenDeadlineAt: event.screenDeadlineAt,
              }
            : current,
        );
        break;
      case 'controls':
        // Another presenter's laptop toggled. No results in this payload for
        // the presenter view to take — theirs are never held.
        if (event.currentScreenIndex !== screenIndexRef.current) break;
        setQuiz((current) =>
          current
            ? {
                ...current,
                resultsVisible: event.resultsVisible,
                votingClosed: event.votingClosed,
                // #118 — merged like the two above: another laptop opening or
                // closing Q&A has to reach this one's toggle.
                qandaOpen: event.qandaOpen,
              }
            : current,
        );
        break;
      case 'state':
        setResults(event.state.results ?? []);
        setServerTime(event.state.serverTime);
        setQuiz((current) =>
          current
            ? {
                ...current,
                status: event.state.status,
                currentScreenIndex: event.state.currentScreenIndex,
                resultsVisible: event.state.resultsVisible,
                votingClosed: event.state.votingClosed,
                screenDeadlineAt: event.state.screenDeadlineAt,
                qandaOpen: event.state.qandaOpen,
                presentedAskedQuestionId: event.state.presentedAskedQuestionId,
              }
            : current,
        );
        break;
      case 'ended':
        setQuiz((current) => (current ? { ...current, status: 'draft' } : current));
        break;
      default:
        // #118's six pushes. Forwarded rather than handled here: this switch
        // owns the screen and the numbers, and the Q&A queue is the panel's
        // state. Falling through silently is what a permissive default would
        // do, and it is exactly how a feed stops updating unnoticed.
        qandaHandlerRef.current?.(event);
        break;
    }
    // The panel also wants the events this switch consumed — 'state' carries
    // the flag its toggle renders — so they are forwarded too, after the
    // switch has taken what it needs.
    if (event.type === 'state') qandaHandlerRef.current?.(event);
  }, []);

  const connection = useLiveQuiz({
    target: quiz?.status === 'live' ? { quizId } : null,
    view: 'presenter',
    onEvent,
    onResync: resync,
  });

  // Above the early returns below, because hooks cannot sit after them. The
  // presenter watches the same clock the room does, off the same deadline.
  const countdown = useCountdown(quiz?.screenDeadlineAt, serverTime);

  // The event key is passed to `run` rather than fired from each onClick so a
  // control that failed is not counted as used — every one of these buttons
  // can come back a validation error, and a dashboard that counts attempts
  // would report a broken presenter as a busy one.
  async function run(action: () => Promise<Quiz>, event?: AdminEventKey) {
    setError(null);
    setBusy(true);
    try {
      setQuiz(await action());
      if (event) track(event);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'validation_failed') {
        const details = caught.details as { path: string; message: string }[] | undefined;
        setError(details?.[0] ? `${details[0].path}: ${details[0].message}` : caught.message);
      } else {
        setError(
          caught instanceof ApiError
            ? caught.message
            : "That didn't work. Try again — if it keeps failing, check your connection.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!quiz) {
    return (
      <section>
        <FormError message={error ?? 'Poll not found.'} />
        <Link to="/polls">Back to polls</Link>
      </section>
    );
  }

  const isLive = quiz.status === 'live';
  const atFirst = quiz.currentScreenIndex === 0;
  const atLast = quiz.currentScreenIndex >= quiz.screens.length - 1;
  const screenUrl = `${window.location.origin}/screen/${quiz.quizId}`;
  // Absent on a poll saved before the flags existed — read as today's
  // behaviour, exactly as the server reads them.
  const held = quiz.resultsVisible === false;
  // Two reasons a question stops taking answers, and they are not equally
  // reversible. The flag is the presenter's own and toggles freely; an expired
  // clock does not, because the server refuses a late vote off the deadline and
  // reopening the flag would not move it. Adding time is a separate control
  // this story does not ship, so the two are reported separately below rather
  // than collapsed into one "closed" the button would then lie about.
  const expired = countdown?.expired === true;
  const closedByFlag = quiz.votingClosed === true;

  return (
    <section>
      <div className="page-head">
        <div>
          <Link to={`/polls/${quiz.quizId}`} className="muted">
            ← Back to poll
          </Link>
          <h1>{quiz.title}</h1>
          <p className="muted">
            {isLive
              ? `Screen ${quiz.currentScreenIndex + 1} of ${quiz.screens.length} · join code ${quiz.joinCode}`
              : 'Not presenting'}
          </p>
          {/* #77 — the same clock the room is watching, so the presenter can
              time a cue against it rather than against the projector. */}
          {isLive && countdown ? (
            <p className={`presenter-countdown${countdown.expired ? ' expired' : ''}`} role="timer">
              {countdown.expired ? "Time's up on this screen" : `${countdown.label} left`}
            </p>
          ) : null}
        </div>
        <div className="page-head-actions">
          <a className="button secondary" href={screenUrl} target="_blank" rel="noreferrer">
            Open big screen
          </a>
          {isLive ? (
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() => {
                if (confirm('End the presentation? The join code stops working.')) {
                  void run(() => quizApi.endPresentation(quiz.quizId), 'presentationEnded');
                }
              }}
            >
              End presentation
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(() => quizApi.startPresentation(quiz.quizId), 'presentationStarted')
              }
            >
              Start presentation
            </button>
          )}
        </div>
      </div>

      <FormError message={error} />

      {!isLive ? (
        <div className="panel empty-state">
          <h2>Ready when you are</h2>
          <p className="muted">
            Starting activates the join code and puts the first screen up. Open the big screen on
            the projector before you start.
          </p>
        </div>
      ) : (
        <>
          <div className="panel presenter-controls">
            <button
              type="button"
              className="secondary"
              disabled={busy || atFirst}
              onClick={() =>
                void run(
                  () => quizApi.goToScreen(quiz.quizId, quiz.currentScreenIndex - 1),
                  'screenAdvanced',
                )
              }
            >
              ← Back
            </button>

            <div className="cue-dots" aria-hidden="true">
              {quiz.screens.map((screen, index) => (
                <span
                  key={screen.screenId}
                  className={index === quiz.currentScreenIndex ? 'cue active' : 'cue'}
                />
              ))}
            </div>

            <span className="presenter-position">
              {quiz.currentScreenIndex + 1} / {quiz.screens.length}
            </span>

            {/* Deliberately disabled at the last screen: ending is only ever
                the explicit End action (§5). */}
            <button
              type="button"
              disabled={busy || atLast}
              aria-describedby={atLast ? 'last-screen-hint' : undefined}
              onClick={() =>
                void run(
                  () => quizApi.goToScreen(quiz.quizId, quiz.currentScreenIndex + 1),
                  'screenAdvanced',
                )
              }
            >
              Next →
            </button>

            {atLast ? (
              <span className="muted" id="last-screen-hint">
                Last screen — use End presentation to finish.
              </span>
            ) : null}

            {connection !== 'live' ? (
              <span className="muted" role="status">
                {connection === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
              </span>
            ) : (
              <span className="live-dot" role="status" aria-label="Live">
                Live
              </span>
            )}
          </div>

          {/* Story #49 — the "guess first" moment. Separate controls, not one:
              a room can be asked to commit before it sees the split (hold), and
              a question can be shut so late votes stop landing (close). */}
          <div className="panel presenter-controls">
            <button
              type="button"
              className="secondary"
              disabled={busy}
              aria-pressed={held}
              onClick={() =>
                void run(
                  () => quizApi.setPresentationControls(quiz.quizId, { resultsVisible: held }),
                  // `held` is the state the button is offering to leave, so
                  // pressing it while held is a reveal.
                  held ? 'resultsRevealed' : 'resultsHeld',
                )
              }
            >
              {held ? 'Reveal results' : 'Hold results'}
            </button>

            <button
              type="button"
              className="secondary"
              // Bound to the flag, not the combined state: the clock running
              // out is not something this button can undo.
              disabled={busy}
              aria-pressed={closedByFlag}
              onClick={() =>
                void run(
                  () =>
                    quizApi.setPresentationControls(quiz.quizId, { votingClosed: !closedByFlag }),
                  'votingClosed',
                )
              }
            >
              {closedByFlag ? 'Reopen voting' : 'Close voting'}
            </button>

            <span className="muted" role="status">
              {held
                ? 'The big screen is holding — you can still see the numbers below.'
                : 'The big screen is showing the results.'}
              {/* Which of the two, because only one of them is a thing the
                  presenter did — and only one of them this button can undo. */}
              {expired
                ? ' The clock has run out, so this screen takes no more votes.'
                : closedByFlag
                  ? ' Voting is closed on this screen.'
                  : null}
            </span>
          </div>

          <div className="panel">
            <h2>Votes coming in</h2>
            <ResultBars questions={results} animated />
          </div>

          {/* #118 — under the ballot's numbers, because the vote is what the
              screen is for and Q&A is what fills the gaps between screens. */}
          <QandaPanel
            quizId={quiz.quizId}
            qandaOpen={quiz.qandaOpen === true}
            busy={busy}
            onToggleOpen={(open) =>
              void run(() => quizApi.setPresentationControls(quiz.quizId, { qandaOpen: open }))
            }
            onRegisterHandler={registerQandaHandler}
            onRegisterReload={registerQandaReload}
          />
        </>
      )}
    </section>
  );
}
